import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { AlertTriangle, ArrowRight, Calendar, Check, Copy, ExternalLink, Eye, EyeOff } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../../Shared/api';
import { assetUrl } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { Event, Project } from '../../../Shared/types';
import { AButton, Button, LinkButton } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import { useCopy } from '../../../Shared/useCopy';
import { useRouter } from '../../router';
import { SharedProps } from '../../types';
import Container from '../../UI/Container';
import Layout from '../../UI/Layout';
import ProjectIcon from './ProjectIcon';
import { ProjectLayout } from './ProjectLayout';

export interface ProjectPageProps extends SharedProps {
  project: Project & {
    donations_count: number;
    event: Event | null;
    create_campaign_url: string;
    deletable: boolean;
  };
}

export default function ProjectPage({
  project,
}: ProjectPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();
  const { formatCurrency, formatDate, formatNumber } = useLocale();

  const [deleteProject, { loading: deleting }] = useMutation(routes.delete_project(project.id), {
    onSuccess: () => {
      notifier.notify(t('backend:project_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  const [copyCreateCampaignUrl, copiedCreateCampaignUrl] = useCopy(project.create_campaign_url);

  const [toggleProject, { loading }] = useMutation(routes.toggle_project(project.id), {
    preserveScroll: true,
    onSuccess: () => {
      notifier.notify(
        !project.enabled ? t('backend:project_enabled') : t('backend:project_disabled'),
        !project.enabled ? Eye : EyeOff
      );
    },
  });

  return (
    <Container>
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-3 space-y-4 text-slate-600" style={{ minWidth: 0 }}>
          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:project_type')}
            </h4>
            <ProjectIcon project={project} className="mr-1" />
            {t(`backend:project_types.${project.type}`)}
          </div>

          {project.event && (
            <>
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:event')}
                </h4>
                <Link href={routes.event_page(project.event.id)}>
                  <Icon className="mr-1">
                    <Calendar />
                  </Icon>
                  {project.event.title}
                </Link>
              </div>
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:date')}
                </h4>
                {formatDate(project.event.start_date)}
              </div>
            </>
          )}

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:campaigns')}
            </h4>
            {formatNumber(project.campaigns_count)}
            {project.campaigns_count > 0 && (
              <span className="text-slate-400">
                {' · '}
                <Link href={`${routes.campaigns_page()}?project_id=${project.id}`}>
                  {t('backend:view')}
                </Link>
              </span>
            )}
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:donations')}
            </h4>
            {formatNumber(project.donations_count)}
            {project.donations_count > 0 && (
              <span className="text-slate-400">
                {' · '}
                <Link href={`${routes.donations_page()}?project_id=${project.id}`}>
                  {t('backend:view')}
                </Link>
              </span>
            )}
          </div>
          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:raised')}
            </h4>
            {formatCurrency(project.funds_raised)}
          </div>

          {project.deletable && (
            <div>
              <ConfirmButton
                onClick={() => deleteProject()}
                loading={deleting}
                variant="danger"
                size="sm"
              >
                {t('backend:delete_project')}
              </ConfirmButton>
            </div>
          )}
        </div>

        <div className="col-span-9">
          <Card panels header>
            <div>
              <h3>
                {project.ended ? t('backend:project_ended') : t('backend:actions')}
              </h3>
            </div>
            {project.ended && (
              <Step icon="project-icon-ended.svg">
                <h4>
                  {t('backend:final_amount', { value: formatCurrency(project.funds_raised) })}
                  {' '}
                  <span className="font-normal text-slate-400 text-sm ml-1">
                    {t('backend:optional_parentheses')}
                  </span>
                </h4>
                <div className="mb-2">
                  {t('backend:project_ended_description')}
                </div>
                <p>
                  <LinkButton href={`${routes.dashboard_page()}?project=${project.id}`} variant="secondary">
                    {t('backend:view_statistics')}
                    <Icon className="ml-1">
                      <ArrowRight />
                    </Icon>
                  </LinkButton>
                </p>
              </Step>
            )}
            {!project.ended && (
              <>
                <Step icon="project-icon-settings.svg">
                  <h4>
                    {t('backend:configure_project')}
                    {' '}
                    <span className="font-normal text-slate-400 text-sm ml-1">
                      {t('backend:optional_parentheses')}
                    </span>
                  </h4>
                  <div className="mb-2">
                    {t('backend:configure_project_description')}
                  </div>
                  <LinkButton href={routes.edit_project_page(project.id)} variant="secondary">
                    {t('backend:settings')}
                    <Icon className="ml-1">
                      <ArrowRight />
                    </Icon>
                  </LinkButton>
                </Step>
                <Step icon="project-icon-mails.svg">
                  <h4>
                    {t('backend:set_project_mails')}
                    {' '}
                    <span className="font-normal text-slate-400 text-sm ml-1">
                      {t('backend:optional_parentheses')}
                    </span>
                  </h4>
                  <div className="mb-2">
                    {t('backend:set_project_mails_descripton')}
                  </div>
                  <LinkButton href={routes.project_mails_page(project.id)} variant="secondary">
                    {t('backend:mails')}
                    <Icon className="ml-1">
                      <ArrowRight />
                    </Icon>
                  </LinkButton>
                </Step>
                <Step icon="project-icon-embeds.svg">
                  <h4>
                    {t('backend:show_campaigns_on_website')}
                    {' '}
                    <span className="font-normal text-slate-400 text-sm ml-1">
                      {t('backend:optional_parentheses')}
                    </span>
                  </h4>
                  <div className="mb-2">
                    {t('backend:show_campaigns_on_website_description')}
                  </div>
                  <LinkButton href={routes.embeds_page()} variant="secondary">
                    {t('backend:embeds')}
                    <Icon className="ml-1">
                      <ArrowRight />
                    </Icon>
                  </LinkButton>
                </Step>
                {project.enabled && project.open && (
                  <Step icon="project-icon-fundraisers.svg">
                    <h4>
                      {t('backend:recruit_campaigners')}
                    </h4>
                    <div className="mb-2">
                      {t('backend:recruit_campaigners_description')}
                    </div>
                    <div className="flex space-x-2">
                      <Input
                        type="text"
                        value={project.create_campaign_url}
                        readOnly
                        onFocus={(event) => event.target.select()}
                      />
                      <Button
                        onClick={() => copyCreateCampaignUrl()}
                        variant="secondary"
                        className={classNames(copiedCreateCampaignUrl && '!text-slate-400')}
                        title={t('backend:copy_signup_link')}
                      >
                        <Icon className="mr-1">
                          {copiedCreateCampaignUrl ? <Check /> : <Copy />}
                        </Icon>
                        {t('backend:copy')}
                      </Button>
                      <AButton
                        href={project.create_campaign_url}
                        target="_blank" rel="noopener noreferrer"
                        variant="secondary"
                        data-supporta-embed="false"
                        title={t('backend:open_signup_link')}
                      >
                        <Icon className="mr-1">
                          <ExternalLink />
                        </Icon>
                        {t('backend:open')}
                      </AButton>
                    </div>
                  </Step>
                )}
                {!project.enabled && (
                  <Step icon="project-icon-launch.svg">
                    <h4>
                      {t('backend:launch_project')}
                    </h4>
                    <div className="mb-2">
                      {t('backend:launch_project_description')}
                    </div>
                    <Button
                      onClick={() => toggleProject({ enabled: true })}
                      variant="secondary"
                      disabled={loading}
                    >
                      {!project.enabled ? t('backend:enable_project') : t('backend:project_enabled')}
                    </Button>
                  </Step>
                )}
              </>
            )}
          </Card>
        </div>
      </div>
    </Container>
  );
}

const Step = ({
  icon,
  children,
}: {
  icon: string;
  children?: ReactNode;
}) => {
  return (
    <div className="grid grid-cols-[96px_1fr] gap-6 sm:gap-8">
      <div>
        <img src={assetUrl(`images/${icon}`)} className="block w-24 h-24" />
      </div>
      <div>
        {children}
      </div>
    </div>
  );
};

ProjectPage.layout = (page: ReactNode) => <Layout><ProjectLayout>{page}</ProjectLayout></Layout>;
